import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { AuthService } from "../services/AuthService";
import {
  MdPhone,
  MdEmail,
  MdLocationOn,
  MdWc,
  MdCalendarMonth,
} from "react-icons/md";
import useModalCloseHook from "../hooks/useModalCloseHook";
import { ProfileImage } from "../Assets/common";

const InfoCard = ({ label, value, icon: Icon, className }) => (
  <div
    className={`
      flex items-start gap-3
      p-3 sm:p-4
      rounded-lg
      bg-white/70 backdrop-blur-sm
      border border-gray-200
      shadow-sm hover:shadow-md transition
      ${className}
    `}
  >
    <div className="flex items-center justify-center w-9 h-9 rounded-full bg-blue-200 text-blue-600 shrink-0">
      <Icon size={18} />
    </div>
    <div className="min-w-0">
      <p className="text-xs font-semibold text-gray-500 uppercase">
        {label}
      </p>
      <p className="text-gray-800 font-medium break-words">
        {value || "N/A"}
      </p>
    </div>
  </div>
);

const MyProfileModal = ({ isOpen, onClose }) => {
  const user = useSelector((state) => state.auth.user);
  const modalRef = useRef(null);
  useModalCloseHook(modalRef, onClose);
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    if (isOpen) {
      const fetchUser = async () => {
        try {
          const data = await AuthService.getUser();
          setUserData(data);
        } catch (error) {
          console.error("Error fetching user:", error.message);
        }
      };
      fetchUser();
    }
  }, [isOpen]);

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3 sm:p-6">
      <div
        ref={modalRef}
        className="
          relative
          bg-white
          rounded-xl sm:rounded-2xl
          w-full max-w-2xl
          max-h-[90vh]
          flex flex-col
          shadow-lg
          overflow-hidden
        "
      >
        {/* Header */}
        <div className="relative bg-gradient-to-r from-blue-700 to-blue-900 h-14 shrink-0">
          <button
            className="absolute top-3 right-4 text-white text-xl hover:text-gray-200"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto">
          {/* Profile Section */}
          <div className="flex flex-col items-center -mt-8 px-4 sm:px-6">
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full border-4 mt-10 border-white shadow-md overflow-hidden bg-gray-100">
              <img
                src={userData?.profile || ProfileImage}
                alt="Profile"
                className="w-full h-full object-cover"
              />
            </div>

            <h3 className="mt-2 text-lg sm:text-xl font-semibold text-center">
              {userData?.name || "N/A"}
            </h3>
            <p className="text-gray-500 text-sm sm:text-[15px] text-center break-words">
              {userData?.email || "No Email"}
            </p>
          </div>

          {/* Info Section */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 px-4 sm:px-6 py-6">
            <InfoCard
              label="Mobile No"
              value={userData?.mobileNumber}
              icon={MdPhone}
            />
            <InfoCard
              label="Date of Birth"
              value={userData?.dateOfBirth}
              icon={MdCalendarMonth}
            />
            <InfoCard
              label="Email"
              value={userData?.email}
              icon={MdEmail}
            />
            <InfoCard
              label="Gender"
              value={userData?.gender}
              icon={MdWc}
            />
            <InfoCard
              label="Address"
              value={userData?.address}
              icon={MdLocationOn}
              className="sm:col-span-2"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyProfileModal;
